import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Copy, Mail, Plus, Send, Ban, Loader2 } from 'lucide-react';
import { API } from '@/App';

const STATUS = {
  draft: { text: 'Link sent', cls: 'bg-blue-500/20 text-blue-300' },
  submitted: { text: 'Submitted', cls: 'bg-amber-500/20 text-amber-300' },
  changes_requested: { text: 'Sent back', cls: 'bg-orange-500/20 text-orange-300' },
  revoked: { text: 'Revoked', cls: 'bg-red-500/20 text-red-300' },
};

const EMPTY = {
  advertiser_name: '', advertiser_email: '', company: '', slot: '',
  billing_period: 'month', duration_days: 30, duration_months: 1,
  price: '', free: false, allow_internal: false, note: '',
};

/** Admin · invite an outside advertiser to build their own ad. */
export default function AdminAdInvitations({ prefill = null }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [invitations, setInvitations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  // Coming in from a request in the Requests block — open the form already filled in.
  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...EMPTY,
      advertiser_name: prefill.name || '',
      advertiser_email: prefill.email || '',
      company: prefill.company || '',
      slot: prefill.slot || f.slot || '',
    }));
    setShowForm(true);
  }, [prefill]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, sl] = await Promise.all([
        axios.get(`${API}/admin/ad-invitations`, { headers }),
        axios.get(`${API}/admin/spotlights/slots`, { headers }),
      ]);
      setInvitations(inv.data.invitations || []);
      setSlots(sl.data.slots || []);
    } catch {
      toast.error('Could not load advertiser invitations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const chosenSlot = slots.find((s) => s.key === form.slot);

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.free ? 0 : Number(form.price || 0) || null,
        duration_days: Number(form.duration_days) || 0,
        duration_months: Number(form.duration_months) || 0,
      };
      const res = await axios.post(`${API}/admin/ad-invitations`, payload, { headers });
      toast.success(`Invitation emailed to ${res.data.advertiser_email}`);
      navigator.clipboard?.writeText(res.data.link).catch(() => {});
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create that invitation');
    } finally { setSaving(false); }
  };

  const act = async (id, what) => {
    try {
      await axios.post(`${API}/admin/ad-invitations/${id}/${what}`, {}, { headers });
      toast.success(what === 'resend' ? 'Link emailed again' : 'Invitation revoked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That did not work');
    }
  };

  const valid = form.advertiser_name.trim()
    && /\S+@\S+\.\S+/.test(form.advertiser_email)
    && form.slot
    && (form.free || Number(form.price) > 0 || form.price === '');

  return (
    <Card className="bg-white/5 border-white/10" data-testid="admin-ad-invitations">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white">Advertiser invitations</CardTitle>
          <p className="mt-1 text-xs text-gray-400">
            Send someone a private link and they build their own ad — no account needed. It comes
            back here for your approval.
          </p>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="bg-amber-500 text-slate-900 hover:bg-amber-400"
          data-testid="ad-invite-new-btn"
        >
          <Plus className="mr-1 h-4 w-4" /> {showForm ? 'Close' : 'New invitation'}
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {showForm && (
          <div className="space-y-4 rounded-2xl border border-white/10 bg-black/20 p-5" data-testid="ad-invite-form">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs text-gray-400">Advertiser name</Label>
                <Input value={form.advertiser_name} onChange={set('advertiser_name')}
                       className="mt-1 border-white/10 bg-white/5" data-testid="ad-invite-name" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Their email</Label>
                <Input type="email" value={form.advertiser_email} onChange={set('advertiser_email')}
                       className="mt-1 border-white/10 bg-white/5" data-testid="ad-invite-email" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Business name</Label>
                <Input value={form.company} onChange={set('company')}
                       className="mt-1 border-white/10 bg-white/5" data-testid="ad-invite-company" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Which spot?</Label>
                <Select value={form.slot} onValueChange={(v) => setForm((f) => ({ ...f, slot: v }))}>
                  <SelectTrigger className="mt-1 border-white/10 bg-white/5" data-testid="ad-invite-slot">
                    <SelectValue placeholder="Choose a placement…" />
                  </SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-400">How long?</Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    type="number" min="1"
                    value={form.billing_period === 'month' ? form.duration_months : form.duration_days}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      [f.billing_period === 'month' ? 'duration_months' : 'duration_days']: e.target.value,
                    }))}
                    className="border-white/10 bg-white/5" data-testid="ad-invite-duration"
                  />
                  <Select value={form.billing_period}
                          onValueChange={(v) => setForm((f) => ({ ...f, billing_period: v }))}>
                    <SelectTrigger className="w-32 border-white/10 bg-white/5" data-testid="ad-invite-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">days</SelectItem>
                      <SelectItem value="month">months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-400">
                  Price {!form.free && <span className="text-gray-600">— blank uses the list price</span>}
                </Label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={set('price')}
                       disabled={form.free} placeholder="Uses the rate card"
                       className="mt-1 border-white/10 bg-white/5 disabled:opacity-40"
                       data-testid="ad-invite-price" />
              </div>
            </div>

            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm text-gray-300" data-testid="ad-invite-free-label">
                <input type="checkbox" checked={form.free} className="h-4 w-4 accent-amber-500"
                       onChange={(e) => setForm((f) => ({ ...f, free: e.target.checked }))}
                       data-testid="ad-invite-free" />
                Complimentary — no card needed
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.allow_internal} className="h-4 w-4 accent-amber-500"
                       onChange={(e) => setForm((f) => ({ ...f, allow_internal: e.target.checked }))}
                       data-testid="ad-invite-allow-internal" />
                They also trade here — may link to a page inside the app
              </label>
            </div>

            {chosenSlot?.image && (
              <p className="text-[11px] text-gray-500" data-testid="ad-invite-slot-spec">
                We&apos;ll ask them for {chosenSlot.image.width} × {chosenSlot.image.height}px
                ({chosenSlot.image.ratio}). {chosenSlot.image.note}
              </p>
            )}

            <div>
              <Label className="text-xs text-gray-400">Note for them</Label>
              <Textarea rows={2} value={form.note} onChange={set('note')}
                        placeholder="Anything you want them to know before they start"
                        className="mt-1 border-white/10 bg-white/5" data-testid="ad-invite-note" />
            </div>

            <Button onClick={create} disabled={!valid || saving}
                    className="bg-amber-500 text-slate-900 hover:bg-amber-400 disabled:opacity-40"
                    data-testid="ad-invite-send-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              Email the invitation
            </Button>
          </div>
        )}

        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-amber-300" />
        ) : invitations.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="ad-invite-empty">
            No invitations yet. Create one and we&apos;ll email the advertiser a private link.
          </p>
        ) : (
          <div className="space-y-2" data-testid="ad-invite-list">
            {invitations.map((inv) => {
              const badge = STATUS[inv.status] || STATUS.draft;
              return (
                <div key={inv.id}
                     className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-4"
                     data-testid={`ad-invite-row-${inv.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-white">
                      {inv.advertiser_name}
                      {inv.company ? <span className="text-gray-400"> · {inv.company}</span> : null}
                    </p>
                    <p className="text-xs text-gray-400">
                      {inv.slot_label} · {inv.run_label} ·{' '}
                      {inv.free ? 'complimentary' : `$${Number(inv.price).toFixed(2)}`} ·{' '}
                      expires {String(inv.expires_at).slice(0, 10)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={badge.cls}>{badge.text}</Badge>
                    <Button size="sm" variant="ghost" className="text-gray-300"
                            onClick={() => {
                              navigator.clipboard?.writeText(inv.link);
                              toast.success('Link copied');
                            }}
                            data-testid={`ad-invite-copy-${inv.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-gray-300"
                            onClick={() => act(inv.id, 'resend')}
                            data-testid={`ad-invite-resend-${inv.id}`}>
                      <Send className="h-4 w-4" />
                    </Button>
                    {inv.status !== 'revoked' && (
                      <Button size="sm" variant="ghost" className="text-red-300"
                              onClick={() => act(inv.id, 'revoke')}
                              data-testid={`ad-invite-revoke-${inv.id}`}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
